/**
 * liveTuneStore — module-level reactive store for live-mode tunables.
 *
 * Shape: see DEFAULTS below. All live-mode components (Scene, EnvProbe,
 * ContactShadow, CameraControllerSync, the environment splat, App.jsx Canvas) read from
 * this store so LiveTunePanel can edit values in real time without a
 * reload. Pattern mirrors graphState / debugState / animVariables — a
 * plain module-level singleton with subscribe/notify.
 *
 * Persistence:
 *   localStorage key `aos-livetune-v5`. URL params seed initial state on
 *   first load (no stored state). On subsequent loads the stored value
 *   wins so a hard-reload after tweaking doesn't silently revert because
 *   the URL still has an old value.
 *
 * Shareable URLs still work on a fresh localStorage.
 */

// Scene (3D) color defaults live in their own module — see that file's header
// for why they are hex literals rather than --m-* tokens.
import {
  SCENE_WHITE, BACKDROP_SKY, BACKDROP_GROUND,
} from './sceneColorDefaults.js'
// Blueprint-authored paths must not persist as the dev's own settings.
import { authoredPathsIn, stripPaths } from './tuneAuthored.js'
// Lights are objects in a map (ENG-1190); this owns their defaults, the
// normalization that completes a partial entry, and the migration from the
// fixed slots (spotlight/spotlight2/spotlight3/torch/hemisphere + shadow's sun).
import {
  defaultLight, normalizeLights, migrateLegacyLights, hasLegacyLightKeys,
  nextLightId, nextLightName,
} from './lightTypes.js'

// v5 (ENG-1190): the fixed light slots — `spotlight`, `spotlight2`, `spotlight3`,
// `torch`, `hemisphere`, and the sun fields of `shadow` — are GONE, replaced by
// the `lights` map. A v4 blob is read once, migrated (lightTypes.js) and written
// back under this key. Bumped rather than merged so a stale slot can't ride
// along as dead state.
// v4 (ENG-814 Phase 2b): the pbr / pbrSkin slices are GONE. They were global
// multipliers over a legacy material's authored THREE props
// (roughness/metalness/normalScale/thickness), and every material is now a node
// graph whose look comes from its own nodes — there is nothing left for a
// global scalar to scale. Bumped rather than left to merge: a persisted v3
// carries those two branches, and silently keeping dead state in the saved tune
// is how a knob comes back as a ghost. (v3 itself dropped v2's normalScale 1.3 /
// thickness 0.5, which had silently overridden the per-material inspector.)
const STORAGE_KEY = 'aos-livetune-v5'
const LEGACY_STORAGE_KEY = 'aos-livetune-v4'
// No bundled default splat: environments are per-character server assets
// (ENG-529) and the old bundled studio.ply was removed. Pointing the default
// at that deleted path made the dev server return SPA-fallback HTML, which got
// cached and fed to Spark as a bogus splat. null = no environment until one is
// chosen from the server-backed ENVIRONMENT dropdown.
const DEFAULT_SPLAT_URL = null

export const DEFAULTS = Object.freeze({
  environment: {
    splatUrl: DEFAULT_SPLAT_URL,
    splatVersion: null, // environment_assets updatedAt — the splat cache key, so a
                        // re-upload under the same url misses and re-downloads
    // The environment_assets row id, as the SERVER stated it. Held because the
    // bake endpoints are keyed on it and several call sites read it
    // synchronously during render. It used to be recovered by regex from
    // splatUrl, which returned nothing for org-layout URLs and silently
    // disabled the Build actions (ENG-999) — an id is data to carry, not a
    // thing to re-derive from a path. null until known (a ?splat= deep link
    // resolves it from the environment list; see environmentFiles.js).
    //
    // Written EPHEMERALLY (setStateEphemeral), never persisted: it is
    // server-derived, so it must not masquerade as a setting the user chose —
    // and persisting it snapshots the WHOLE tune as a side effect, which then
    // beats URL params on the next navigation (that silently disabled ?probe=1).
    assetId: null,
    // floorY: explicit avatar-floor plane (null = auto-detect from splat).
    pose: { px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, scale: 1, floorY: null },
    // The splats this environment is made of (ENG-1132), as a LIST of
    // {name, url, pose, tags, …}. `[]` — every environment before that ticket
    // — renders splatUrl alone, which is what partMounts does with no parts.
    //
    // A LIST rather than the map the API stores, because deepMerge below merges
    // plain objects and REPLACES arrays: held as a map, switching from a
    // four-part room to a one-part room would merge the two and leave the old
    // room's furniture standing inside the new one.
    //
    // Written EPHEMERALLY, like assetId and for the same reason: it is
    // server-derived, not a setting anyone chose. That means a reload restores
    // splatUrl with NO parts, so Scene re-resolves them from the row before
    // mounting — see useEnvironmentParts. Persisting them instead would make a
    // stale copy win over the server's, which is exactly the failure the
    // write-through-cache rule in CLAUDE.md exists to prevent.
    parts: [],
  },
  camera: {
    fov: 45,
    // Look-at target. mode 'fixed' leaves the target to drei's prop (orbit) or
    // the preset default; 'head' tracks the avatar head; 'vector' pins an [x,y,z].
    // Driven by setCameraTarget.
    target: { mode: 'fixed' },
    // Camera cage: a box (axis-aligned by default, oriented when rx/ry/rz are
    // non-zero) that keeps the camera from dollying out through splat walls.
    // Off by default — enable via the setCameraCage node or the LiveTunePanel
    // when an environment needs it. hx/hy/hz size the box; ox/oy/oz shift the
    // box away from its anchor (0 = centered on the anchor), so the cage can
    // sit anywhere rather than always being symmetric around the avatar.
    // rx/ry/rz (radians, three's default 'XYZ' Euler order — matches every
    // other rotation triple in the app, e.g. environment.pose) tilt the box
    // around that same anchor+offset center, for a room whose walls aren't
    // world-axis-aligned. The anchor is the avatar's own world position —
    // deliberately NOT the orbit look-at point, which a right-drag pans
    // freely: cameraController.apply() uses the anchor+offset as the clamp's
    // SOLE reference point (both the ray origin and the box center), so
    // panning can't drag the box around, and there's no "look-at point fell
    // outside the box" failure mode to worry about — see clampToCage in
    // cameraController.js.
    cage: { enabled: false, hx: 2.5, hy: 1.25, hz: 2.5, margin: 0.5, ox: 0, oy: 0, oz: 0, rx: 0, ry: 0, rz: 0 },
    // See-through: fade environment splats between the camera and the head so
    // geometry doesn't occlude the face when orbiting. ON by default (only ever
    // applies when a splat environment is mounted; a no-op otherwise). Consumed
    // by whichever component owns the environment (`environmentOwner.js`);
    // tunable in the LiveTunePanel or overridable via the setCameraSeeThrough node.
    // auto = let the controller toggle the fade strength from camera geometry
    // (near the head, or below it looking up) instead of a manual on/off. It's a
    // proximity/angle heuristic, not true per-splat occlusion (no cheap splat
    // raycast exists) — see cameraController.apply.
    seeThrough: { enabled: true, radius: 0.6, fade: 0.3, auto: false, autoNear: 1.2, autoBelow: true },
    // Transition durations (ms) for smooth camera moves. 0 = snap. fov eases the field of
    // view; target eases the look-at when the target config changes. Driven by the blendMs
    // pin on setCameraFov / setCameraTarget. (Rig pose easing is per-node — see the
    // Camera Set Rig Pose node's blendMs — not a global, because a scripted move's duration
    // belongs to the move, not to the session.)
    blend: { fov: 0, target: 0 },
    // Follow / framing: a continuous mode that keeps the avatar framed as it
    // moves. offset = camera position relative to the avatar (world, or 'local'
    // = rotated by the avatar's facing so the camera trails behind on turns);
    // damping 0..1 smooths the chase (0 = snap); targetOffsetY biases the
    // look-at up/down for screen framing. Driven by setCameraFollow; overrides
    // the preset's camera pose while enabled.
    follow: { enabled: false, offset: [0, 1.6, 3], space: 'local', damping: 0.12, targetOffsetY: 0 },
  },
  lighting: {
    iblEnabled: true,
    // Rebalanced for ENG-812: the IBL no longer lights the avatar alone. A
    // single analytic key light (keyLightIntensity) is layered on top in live
    // mode so tangent-space normal detail has a high-frequency gradient to
    // catch — a soft equirect IBL on its own reveals almost no pore/wrinkle
    // relief. Lowered from 2.5 so IBL(fill) + key don't double-count to over-bright.
    envMapIntensity: 1.5,
    // Analytic key light (live mode, on top of IBL). Direction comes from the
    // shadow light controls (shadow.lightElevation/Azimuth) so "where the key
    // is" has one source of truth and the existing sun-position sliders drive
    // both shading and the contact shadow. 0 = pure IBL (old behaviour).
    keyLightIntensity: 1.2,
    // Intensity of the fallback 3-light rig's ambient light (Scene.jsx).
    // Defaults to 0 — the flat ambient fill washed out the IBL/shadow look.
    ambientIntensity: 0,
    ambientColor: SCENE_WHITE,
    // Diffuse SH light-probe (LightProbeVolume.jsx). This is baked-splat
    // ambient bounce, driven by the avatar's position — NOT part of the IBL
    // toggle. Multiplier scales THREE.LightProbe.intensity; the enable flag
    // zeroes its contribution without unmounting the probe. Defaults preserve
    // the pre-gate look (fully on, ×1).
    lightProbeEnabled: true,
    lightProbeIntensity: 1,
  },
  // The lights in the scene, id → entry (ENG-1190). Each entry is
  // `{ type: 'directional'|'spot'|'point'|'hemisphere', name, enabled, ...}`;
  // see lightTypes.js for the per-type fields and defaults. A MAP, not an
  // array: deepMerge merges plain objects and REPLACES arrays, and every other
  // piece of store machinery (authoredPathsIn, stripPaths, diffTune, computeDiff)
  // walks objects the same way. Insertion order is the outliner order.
  //
  // Only the sun exists by default — it was the one light that was always on.
  // The old opt-in slots (spotlight ×3, torch, hemisphere) become entries only
  // when someone adds them (or when a saved look that used them migrates).
  // Rendered by SceneLights.jsx; edited from the outliner's Lighting rows.
  lights: {
    sun: defaultLight('directional'),
  },
  backdrop: {
    // ENG-894 — the SKY (viewport background) and GROUND PLANE colors, for
    // sessions with no splat environment: an OGS/GLB avatar on the default
    // canvas has a themed grey background and no floor, so she reads as
    // floating and there's no way to set up a flat colored backdrop (studio
    // grey, white, or a keyable green) for a recording.
    //
    // Both halves are OFF by default and independent:
    //   sky OFF    → scene.background stays null and the themed CSS canvas
    //                background shows through, exactly as before this slice.
    //   sky ON     → scene.background = skyColor, so the color is part of the
    //                WebGL frame (it shows up in a canvas readback / IBL bake,
    //                which a CSS background never did).
    //   ground ON  → an opaque UNLIT plane at the stage floor: groundColor is
    //                exactly what renders. (Lit would read black whenever the
    //                scene has no IBL — ambient + sun intensities both default
    //                to 0 — which defeats the point of a color control.)
    //                It does NOT
    //                receive shadows: ShadowCatcher remains the single shadow
    //                receiver and composites its ShadowMaterial on top, so
    //                enabling the ground can't double-darken the contact
    //                shadow. Turning the catcher off still means no shadow.
    skyEnabled: false,
    skyColor: BACKDROP_SKY,
    groundEnabled: false,
    groundColor: BACKDROP_GROUND,
    groundSize: 12,      // metres, square. Matches ShadowCatcher's planeSize.
    // Hide the environment splat so the backdrop is what you see.
    //
    // Without this the backdrop is only usable in a scene that happens to have
    // no environment: the sky is scene.background (behind ALL geometry) and the
    // ground sits at the splat's own floor, so a room scan covers both and the
    // controls look broken while working correctly. Telling the user to go find
    // the ENVIRONMENT dropdown and set it to none is a worse answer than a
    // switch on the thing they're already using.
    //
    // Visual only — the environment still supplies lighting (EnvProbe /
    // LightProbeVolume / ReflectionCaptureVolume are keyed off the same splat
    // URL and stay mounted), so hiding it doesn't relight the avatar.
    hideEnvironment: false,
  },
  // The shared shadow CATCHER plane only (ShadowCatcher.jsx) — every caster
  // (the sun, the spots) lands on it. The sun's own knobs (intensity,
  // temperature, placement, its shadow map) are `lights.sun` since ENG-1190.
  shadow: {
    opacity: 0.4,
    floorYOverride: null,     // null = auto-detect from splat
  },
  debug: {
    // Reference rig (chrome mirror ball + matte SH probe sphere) is OFF by
    // default — opt in via the panel or ?mirror=1 / ?probe=1. It's a dev
    // oracle, and while mounted it OWNS window.__AVATAR_PROBE_POS__ (moves the
    // IBL sample point to the rig), so defaulting it off also lets the avatar
    // sample the bake at its own position.
    mirrorBall: false,
    probeSphere: false,
  },
})

function deepClone(v) {
  if (Array.isArray(v)) return v.map(deepClone)
  if (v && typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v)) out[k] = deepClone(v[k])
    return out
  }
  return v
}

function deepMerge(base, patch) {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return patch
  const out = { ...base }
  for (const k of Object.keys(patch)) {
    // Guard against prototype pollution from untrusted patches (e.g. the `tp` URL carrier).
    if (k === '__proto__' || k === 'constructor' || k === 'prototype') continue
    if (patch[k] && typeof patch[k] === 'object' && !Array.isArray(patch[k])
        && base && typeof base[k] === 'object' && !Array.isArray(base[k])) {
      out[k] = deepMerge(base[k], patch[k])
    } else {
      out[k] = patch[k]
    }
  }
  return out
}

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

// deepMerge, plus: a patch that touches `lights` leaves every entry whole.
// `setState({ lights: { torch: { enabled: true } } })` on a store with no torch
// would otherwise land a one-key entry that nothing can render; normalizeLights
// fills it from the point-light defaults (the legacy id says which type). Same
// rule for a stored blob or a `tp` carrier missing a field a later build added.
function mergePatch(base, patch) {
  const next = deepMerge(base, patch)
  if (patch && typeof patch === 'object' && 'lights' in patch) {
    next.lights = normalizeLights(next.lights)
  }
  return next
}

// Pure recursive structural diff mirroring deepMerge's walk: returns an object
// containing ONLY the leaves where `current` differs from `base`. Nested plain
// objects recurse and are included only if they have any differing leaf.
// Arrays/primitives compare by value (JSON.stringify for arrays — the values
// here are small POJOs/arrays of numbers/strings, so this is cheap and exact
// enough). Used by LiveTunePanel's Copy URL to build a comprehensive share
// param instead of a hand-picked field list.
export function computeDiff(base, current) {
  if (!isPlainObject(base) || !isPlainObject(current)) return current
  const out = {}
  for (const k of Object.keys(current)) {
    const b = base[k]
    const c = current[k]
    if (isPlainObject(b) && isPlainObject(c)) {
      const sub = computeDiff(b, c)
      if (Object.keys(sub).length) out[k] = sub
    } else {
      const same = (Array.isArray(b) || Array.isArray(c))
        ? JSON.stringify(b) === JSON.stringify(c)
        : b === c
      if (!same) out[k] = c
    }
  }
  return out
}

// Parses both the legacy per-field URL params (splat/sx/sy/.../cage/noibl/
// shadow/shadowY/mirror/probe) AND the comprehensive `tp` (tune patch) JSON
// carrier written by LiveTunePanel's Copy URL. `tp` is applied AFTER the
// legacy params so old bookmarked URLs keep working, and is the
// forward-compatible carrier for anything the panel can edit. Exported so
// tests can drive it directly with a synthetic URLSearchParams.
export function parseTuneUrlParams(params = (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams())) {
  const p = params
  const num = (k) => {
    const v = parseFloat(p.get(k))
    return Number.isFinite(v) ? v : undefined
  }
  let patch = {}

  // Environment
  const splatUrl = p.get('splat')
  const pose = {
    px: num('sx'), py: num('sy'), pz: num('sz'),
    rx: num('srx'), ry: num('sry'), rz: num('srz'),
    scale: p.has('ss') ? num('ss') : undefined,
  }
  const poseEntries = Object.entries(pose).filter(([, v]) => v !== undefined)
  if (splatUrl || poseEntries.length) {
    patch.environment = {}
    if (splatUrl) patch.environment.splatUrl = splatUrl
    if (poseEntries.length) patch.environment.pose = Object.fromEntries(poseEntries)
  }

  // Camera cage: ?cage=0 to disable, ?cage=hx,hy,hz to set
  const cageRaw = p.get('cage')
  if (cageRaw != null) {
    patch.camera = patch.camera ?? {}
    if (cageRaw === '0') {
      patch.camera.cage = { enabled: false }
    } else {
      const parts = cageRaw.split(',').map(Number)
      if (parts.length === 3 && parts.every(v => Number.isFinite(v) && v > 0)) {
        patch.camera.cage = { enabled: true, hx: parts[0], hy: parts[1], hz: parts[2] }
      }
    }
  }

  // Lighting
  if (p.has('noibl')) {
    patch.lighting = { ...(patch.lighting ?? {}), iblEnabled: false }
  }

  // Shadow — `?shadow=0` used to turn the sun off; that flag is now the sun's
  // own `enabled`. A positive value is still the catcher plane's opacity.
  if (p.has('shadow')) {
    const v = parseFloat(p.get('shadow'))
    if (Number.isFinite(v)) {
      if (v <= 0) patch.lights = { ...(patch.lights ?? {}), sun: { enabled: false } }
      else patch.shadow = { ...(patch.shadow ?? {}), opacity: Math.min(1, v) }
    }
  }
  // `?spotshadows=0` and `?shadowmap=N` are DELIBERATELY NOT HANDLED HERE, and
  // that is the fix rather than an omission. `readInitial()` ignores the URL
  // whenever localStorage holds a stored tune ("stored value wins"), and an
  // environment's own `default_tune` is applied asynchronously after boot on top
  // of whatever was there — so routed through the store they parsed correctly and
  // then silently did nothing. They are applied at the light itself
  // (`SpotlightRig.jsx`), where nothing can outrank them. A performance escape
  // hatch that quietly fails is worse than none.

  if (p.has('shadowY')) {
    const v = parseFloat(p.get('shadowY'))
    if (Number.isFinite(v)) {
      patch.shadow = { ...(patch.shadow ?? {}), floorYOverride: v }
    }
  }

  // Debug — the reference spheres are OFF by default (opt-in). `mirror=1` /
  // `probe=1` turn them on for a session or shared URL. Legacy `mirror=0` /
  // `probe=0` are still honoured as explicit-off (no-ops against the new
  // default) so old URLs and the oracle tests that pass them don't break.
  // The probe rig owns window.__AVATAR_PROBE_POS__ while mounted — see DEFAULTS.
  const debug = {}
  if (p.get('mirror') === '1') debug.mirrorBall = true
  else if (p.get('mirror') === '0') debug.mirrorBall = false
  if (p.get('probe') === '1') debug.probeSphere = true
  else if (p.get('probe') === '0') debug.probeSphere = false
  if (Object.keys(debug).length) patch.debug = debug

  // Comprehensive carrier: applied last so it wins over (or extends) any
  // legacy field above. Malformed JSON is silently ignored — a broken `tp`
  // shouldn't break the legacy params that parsed fine.
  if (p.has('tp')) {
    try {
      patch = deepMerge(patch, JSON.parse(p.get('tp')))
    } catch { /* ignore malformed tp */ }
  }

  // A URL copied from a pre-ENG-1190 build carries the fixed light slots;
  // convert them so an old bookmark still lights the scene it described.
  if (hasLegacyLightKeys(patch)) patch = migrateLegacyLights(patch)

  return patch
}

// One-time migration: older sessions persisted splatUrl pointing at the
// removed bundled studio.ply (or any /assets/environments/*.ply — none are
// bundled anymore, ENG-529). That dead path makes the dev server return
// SPA-fallback HTML and hangs the loader. Drop it so the null default (pick
// from the server-backed dropdown) takes over, with zero manual intervention.
function migrate(s) {
  const u = s?.environment?.splatUrl
  if (typeof u === 'string' && /^\/assets\/environments\/.*\.ply$/.test(u)) {
    s.environment.splatUrl = null
  }
  return s
}

// One-time purge of a persisted environment that predates provenance
// (ENG-1031). Until tuneAuthored.js existed, the `loadEnvironment` node wrote
// its environment through the PERSISTING setter, so a blueprint's choice was
// stored as though the dev had picked it — and outlived the node, the graph,
// even the character. Storage records no provenance, so a stored environment
// from before this key existed cannot be attributed: it may be a real pick or a
// blueprint's leftover.
//
// The safe direction is to drop it. "No environment" is visible and one click
// from fixed; "an environment nobody asked for" is what sent Andres looking for
// a node that wasn't there. Runs ONCE per browser, only for a STORED blob (never
// on the URL-seed path, or a `?splat=` deep link would purge itself), writes the
// cleaned blob straight back so a second load can't resurrect the value, and
// says so on the console rather than silently changing the scene.
const ENV_PURGE_KEY = 'aos-livetune-env-purge-v1'

function purgeUnattributedEnvironment(s) {
  try {
    if (localStorage.getItem(ENV_PURGE_KEY)) return s
    const had = s?.environment?.splatUrl
    if (had) {
      s.environment.splatUrl = null
      s.environment.pose = deepClone(DEFAULTS.environment.pose)
      console.warn(
        `[liveTuneStore] dropped the stored environment (${had}) once: before ENG-1031 a ` +
        'loadEnvironment node persisted its choice as if you had picked it, and storage keeps no ' +
        'record of which it was. Pick it again in LIVE TUNE ▸ ENVIRONMENT (that persists), or let ' +
        'the blueprint node load it (that no longer persists).',
      )
    }
    // Written even when there was nothing to drop, so this is once per browser.
    localStorage.setItem(ENV_PURGE_KEY, '1')
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch { /* quota / private mode — a purge we can't record is a purge we skip */ }
  return s
}

// A stored blob's `lights` REPLACES the default map rather than merging into
// it: the one default light (the sun) can be deleted, and "absent from what you
// saved" has to mean deleted, not "please bring it back". Everything else
// merges as before.
function fromStored(stored) {
  const base = deepClone(DEFAULTS)
  const s = migrateLegacyLights(stored) ?? {}
  const next = deepMerge(base, s)
  next.lights = normalizeLights(isPlainObject(s.lights) ? s.lights : base.lights)
  return next
}

function readInitial() {
  if (typeof window === 'undefined') return deepClone(DEFAULTS)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      return purgeUnattributedEnvironment(migrate(fromStored(JSON.parse(raw))))
    }
    // One-time upgrade of a v4 blob: read, convert the fixed light slots into
    // `lights`, write back under the new key, drop the old one.
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (legacy) {
      const s = purgeUnattributedEnvironment(migrate(fromStored(JSON.parse(legacy))))
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
        localStorage.removeItem(LEGACY_STORAGE_KEY)
      } catch { /* quota — next load migrates again, harmlessly */ }
      return s
    }
  } catch { /* ignore corrupted localStorage */ }
  // FIRST LOAD ON THIS BROWSER: no stored blob, so there is nothing legacy to
  // purge — and the marker must end up written, or the purge stays armed and eats
  // the FIRST environment the user picks, on the reload after they pick it. That
  // was the reported bug, and it needs no corrupt data: clear the browser cache,
  // load an environment, reload, and it is gone. The migration exists to drop
  // environments stored BEFORE ENG-1031; a browser with no storage at all has
  // none, and was the one case guaranteed to be purged, because the flag was only
  // ever written on the path that found a blob.
  //
  // IT IS WRITTEN WITH THE BLOB, NOT HERE, and that distinction is the whole of
  // the fix. Writing it on a bare load leaves an `aos-*` key behind on a browser
  // that has stored nothing — which "Clear Cache" then cannot clear, because the
  // store re-initialises on the reload and puts it straight back. The marker
  // answers "has this blob been purged", so it belongs with the blob: `persist()`
  // writes the pair, and a browser with no blob has nothing to answer for.
  return migrate(mergePatch(deepClone(DEFAULTS), parseTuneUrlParams()))
}

let state = readInitial()
const listeners = new Set()

// Session-only (never persisted) set of camera sub-paths the dev has actively
// touched this session — e.g. 'camera.fov', 'camera.cage'. A touched path is a
// dev override that a blueprint node must NOT clobber. Precedence:
//   URL param (dev) > panel session override (dev) > blueprint node > default.
// Empty on every fresh load, so a blueprint always wins over a stale STORED
// value until the dev re-touches the control.
const _touched = new Set()

// camera.<k> paths set by a blueprint node — applied+notified but stripped from persist() so they never masquerade as stored dev settings.
const _ephemeralCamera = new Set()

// Paths whose CURRENT value was NOT chosen by the user, mapped to who did
// choose it: 'blueprint' (a loadEnvironment node, plus the environment's shared
// tune) or 'url' (a `?environment=` deep link). Both apply and notify like any
// other write, and persist() strips both from the stored snapshot — neither may
// come back next load as though the dev had made it (see tuneAuthored.js). A
// later setState on the same path is a user write and clears the mark.
//
// The SOURCE is load-bearing, not bookkeeping: teardown when a node disappears
// must drop a 'blueprint' value and must NOT drop a 'url' one, or opening a
// shared link on a character with no loadEnvironment node would show the
// environment and then immediately unload it.
const _authored = new Map()

// Ephemeral dev-view-aid flag (e.g. "show the camera cage gizmo"). Lives OUTSIDE
// `state` deliberately: it must never be swept into persist()'s whole-state
// localStorage serialization (that's the camera-truth-plumbing leak
// this guards against — a dev-only view toggle silently surviving into a
// shared/persisted snapshot). notify()-only, no persist().
let _cageGizmoVisible = false

// Seed URL-driven camera params as dev touches (top precedence). Any camera
// key present in the URL — the legacy ?cage= field OR anything the `tp`
// carrier delivers (fov, follow, preset, target, seeThrough, blend) — is an
// explicit share and must outrank a blueprint node's write, so mark every one
// touched (not just cage). Mirrors the documented precedence URL > blueprint.
// Exported (and re-runnable) so a unit test can drive it with a synthetic
// patch instead of the module-load window.location it normally reads.
export function seedUrlCameraTouches(urlPatch) {
  for (const k of Object.keys(urlPatch?.camera || {})) _touched.add(`camera.${k}`)
}
if (typeof window !== 'undefined') {
  try {
    // Only treat URL camera params as dev touches when they actually seeded the
    // initial state — i.e. there was no stored state. readInitial() ignores the
    // URL when localStorage exists (stored value wins), so in that case the URL
    // is NOT authoritative this load and marking its keys touched would wrongly
    // block blueprint writes while showing the stored value, not the URL's.
    if (!localStorage.getItem(STORAGE_KEY) && !localStorage.getItem(LEGACY_STORAGE_KEY)) seedUrlCameraTouches(parseTuneUrlParams())
  } catch { /* ignore */ }
}

function persist() {
  if (typeof window === 'undefined') return
  try {
    let toStore = state
    if (_ephemeralCamera.size && state.camera) {
      toStore = deepClone(state)
      for (const path of _ephemeralCamera) delete toStore.camera[path.slice('camera.'.length)]
    }
    if (_authored.size) toStore = stripPaths(toStore, [..._authored.keys()])
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
    // THE MARKER TRAVELS WITH THE BLOB. `purgeUnattributedEnvironment` drops an
    // environment stored before ENG-1031 and runs exactly once per browser, gated
    // on this key — so a blob written WITHOUT it is a blob the purge will eat on
    // the next load, and the first environment a fresh browser picks is written by
    // this very function. Writing it here rather than at load also leaves nothing
    // behind for a browser that has stored nothing, which is what lets "Clear
    // Cache" actually end with no `aos-*` keys.
    localStorage.setItem(ENV_PURGE_KEY, '1')
  } catch { /* quota etc. */ }
}
function notify() {
  if (typeof window !== 'undefined') window.__LIVE_TUNE__ = state
  for (const fn of listeners) fn()
}
function emit() { persist(); notify() }

export const liveTuneStore = {
  getState() { return state },
  setState(patch) {
    // A dev/user write owns the path from now on: drop any blueprint mark so
    // the value persists again (the mirror of setCameraFromPanel clearing
    // _ephemeralCamera).
    for (const p of authoredPathsIn(patch)) _authored.delete(p)
    state = mergePatch(state, patch)
    emit()
  },
  // ── Lights as objects (ENG-1190) ─────────────────────────────────────────
  /** Add a light of `type` (lightTypes.LIGHT_TYPES), ENABLED — a light you just
   *  asked for should light something. Returns its id, so the caller can select
   *  the new outliner row. Spots are pre-spread in yaw like the old slots were,
   *  so a second one does not land on top of the first. */
  addLight(type) {
    const id = nextLightId(state.lights, type)
    const entry = { ...defaultLight(type), name: nextLightName(state.lights, type), enabled: true }
    if (type === 'spot') {
      const spots = Object.values(state.lights).filter(l => l.type === 'spot').length
      entry.yaw = [0, 130, -130, 60, -60][spots % 5]
    }
    this.setState({ lights: { [id]: entry } })
    return id
  },
  /** Remove one light. deepMerge can only ADD to a map, so this is the one
   *  place an entry leaves it. Unknown id: no-op, returns false. */
  removeLight(id) {
    if (!state.lights || !(id in state.lights)) return false
    const lights = { ...state.lights }
    delete lights[id]
    for (const p of [..._authored.keys()]) if (p === `lights.${id}`) _authored.delete(p)
    state = { ...state, lights }
    emit()
    return true
  },
  /** Patch one light's fields. Unknown id: no-op, returns false — a patch must
   *  not conjure a light of no type. */
  setLight(id, patch) {
    if (!state.lights || !(id in state.lights)) return false
    this.setState({ lights: { [id]: patch } })
    return true
  },
  // Non-user write — `source` says who: 'blueprint' (the loadEnvironment node +
  // the shared tune it applies) or 'url' (a `?environment=` deep link). Applies
  // live like setState, but the paths it writes are marked so persist() strips
  // them: the blueprint re-states its choice on every construct and a link
  // re-states it on every visit, so nothing is lost by not storing either — and
  // a deleted node, or a link someone followed once, stops having an effect.
  // See tuneAuthored.js.
  setStateAuthored(patch, { source = 'blueprint' } = {}) {
    for (const p of authoredPathsIn(patch)) _authored.set(p, source)
    state = mergePatch(state, patch)
    emit()
  },
  /**
   * Put every BLUEPRINT-authored key of one slice back to its default.
   *
   * The counterpart to setStateAuthored, and the reason it exists: an authored
   * value has to be REVOKED when the graph that asked for it goes away, or it
   * outlives its author. Setting a background on ANI and then switching to Myra
   * left Myra wearing ANI's background, because nothing fires a `setBackdrop`
   * for a character that has none — the same shape as the environment bug that
   * needed unloadAuthoredEnvironment, which is why the caller is the same kind
   * of reachability watcher.
   *
   * Only 'blueprint' keys revert. A value the USER set (source null) or a deep
   * link set ('url') is left alone, exactly as unloadAuthoredEnvironment refuses
   * to touch a human pick — otherwise switching characters would silently
   * discard someone's own colour.
   *
   * Returns true when something actually changed, so the caller can log
   * meaningfully instead of on every graph edit.
   */
  revertAuthored(slice, { source = 'blueprint' } = {}) {
    if (!(slice in DEFAULTS)) return false
    const prefix = `${slice}.`
    const keys = []
    for (const [path, who] of _authored) {
      if (who === source && path.startsWith(prefix)) keys.push(path.slice(prefix.length))
    }
    if (keys.length === 0) return false
    const next = { ...state[slice] }
    for (const k of keys) {
      // A key with no default (a light the blueprint's environment ADDED) has
      // nothing to revert TO — it goes, rather than staying as an empty shell.
      if (k in DEFAULTS[slice]) next[k] = deepClone(DEFAULTS[slice][k])
      else delete next[k]
      _authored.delete(`${prefix}${k}`)
    }
    state = { ...state, [slice]: next }
    emit()
    return true
  },
  // Apply + notify WITHOUT persisting — used for blueprint-authored values so
  // they don't masquerade as the dev's own localStorage settings next load.
  setStateEphemeral(patch) {
    state = mergePatch(state, patch)
    notify()
  },
  isTouched(path) { return _touched.has(path) },
  // Was this path's current value written by something other than the user?
  isAuthored(path) { return _authored.has(path) },
  // WHO wrote it: 'blueprint' | 'url' | null (the user, or a default). The
  // teardown watcher asks: a node's environment goes when its node goes, while
  // a deep link's and a human's both stay.
  authoredSource(path) { return _authored.get(path) ?? null },
  markTouched(path) { _touched.add(path) },
  // Scoped escape hatch: undo a single touched path (e.g. a node/test wants a
  // blueprint write to apply again without a full liveTuneStore.reset()).
  releaseTouched(path) { _touched.delete(path); notify() },
  // LiveTunePanel camera edits: mark each touched, then persist + notify (dev
  // always wins live once they touch a control).
  setCameraFromPanel(patch) {
    for (const k of Object.keys(patch)) { _touched.add(`camera.${k}`); _ephemeralCamera.delete(`camera.${k}`) }
    // Dev edits snap — clear any blueprint-set ease (blend.*) for the keys being
    // edited so a prior eased blueprint write doesn't rubber-band the panel
    // control (a slider drag would otherwise interpolate over the old duration).
    const reset = {}
    if ('fov' in patch) reset.fov = 0
    if ('target' in patch) reset.target = 0
    const next = Object.keys(reset).length
      ? { ...patch, blend: { ...state.camera.blend, ...reset } }
      : patch
    this.setState({ camera: next })
  },
  // Blueprint node camera writes: skip any path the dev has touched; apply the
  // rest ephemerally (no persist, no touch). Returns which top-level patch keys
  // actually applied vs. were silently dropped by a panel/URL touch, so callers
  // (cameraController setters) can report true success/failure instead of
  // logging "applied" unconditionally (the silent-fakery bug this PR fixes).
  setCameraFromBlueprint(patch) {
    const filtered = {}
    const ignoredKeys = []
    for (const k of Object.keys(patch)) {
      if (_touched.has(`camera.${k}`)) ignoredKeys.push(k)
      else filtered[k] = patch[k]
    }
    for (const k of Object.keys(filtered)) _ephemeralCamera.add(`camera.${k}`)
    if (Object.keys(filtered).length) this.setStateEphemeral({ camera: filtered })
    return { appliedKeys: Object.keys(filtered), ignoredKeys }
  },
  // Dev-only "show the camera cage gizmo" view-aid — ephemeral, never persisted
  // (see _cageGizmoVisible above).
  getCageGizmoVisible() { return _cageGizmoVisible },
  setCageGizmoVisible(v) { _cageGizmoVisible = !!v; notify() },
  subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },
  reset() {
    state = deepClone(DEFAULTS)
    _touched.clear()
    _ephemeralCamera.clear()
    _authored.clear()
    // "Reset all" clears dev view-aids too, not just tunable state — a stale
    // gizmo left on after a reset would be confusing (deliberate choice; it's
    // ephemeral anyway so this only affects the current session).
    _cageGizmoVisible = false
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    }
    emit()
  },
  // Reset a single top-level slice back to its default (e.g. "Reset" on just
  // the SPOTLIGHT section) without touching any other slice. No-op on an
  // unknown key so a typo can't create junk state. Resetting 'camera' also
  // clears _touched so a blueprint camera write can apply again — mirrors
  // reset()'s full-clear behavior, scoped to the one slice.
  resetSlice(key) {
    if (!(key in DEFAULTS)) return
    state = { ...state, [key]: deepClone(DEFAULTS[key]) }
    if (key === 'camera') for (const p of [..._touched]) if (p.startsWith('camera.')) _touched.delete(p)
    emit()
  },
}

// Publish initial snapshot on window for debug. Also expose the store
// itself so tests and the console can mutate state without an import.
if (typeof window !== 'undefined') {
  window.__LIVE_TUNE__ = state
  window.__LIVE_TUNE_STORE__ = liveTuneStore
}
